'use client';
/**
 * components/OttoBanner.tsx — OGRYN
 *
 * - compact : 150px · demi-écran : 300px
 * - Micro : enregistrement audio via MediaRecorder API
 * - Aucun import Leaflet
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useMobile } from '@/contexts/MobileContext';

type BannerSize = 'compact' | 'half';

interface Message {
  id:        string;
  role:      'ai' | 'user';
  text?:     string;
  audioUrl?: string; // message vocal
}

const INITIAL: Message[] = [
  { id: '0', role: 'ai', text: 'Bonjour ! Une urgence ou une question ?' },
];

const NAV_H = 56;

// ── SVG icons ──────────────────────────────────────────────────────────────────

function IconExpand() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15"/>
    </svg>
  );
}

function IconCollapse() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function IconSend() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
    </svg>
  );
}

function IconMic({ recording }: { recording: boolean }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={recording ? '#f87171' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function OttoBanner() {
  const { ottoOpen, setOttoOpen } = useMobile();

  const [size,         setSize]         = useState<BannerSize>('compact');
  const [msgs,         setMsgs]         = useState<Message[]>(INITIAL);
  const [input,        setInput]        = useState('');
  // Positionné au-dessus des FABs (NAV_H + 8 + 44 + 8 = 116) par défaut
  const [bottomOffset, setBottomOffset] = useState(116);
  const [isRecording,  setIsRecording]  = useState(false);

  const endRef            = useRef<HTMLDivElement>(null);
  const mediaRecorderRef  = useRef<MediaRecorder | null>(null);
  const audioChunksRef    = useRef<Blob[]>([]);
  const streamRef         = useRef<MediaStream | null>(null);

  const height = size === 'compact' ? 150 : 300;

  // Repositionne au-dessus du clavier iOS/Android
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const keyboardH = window.innerHeight - vv.height - vv.offsetTop;
      setBottomOffset(keyboardH > 50 ? keyboardH + 4 : 116);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, []);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setMsgs(prev => [...prev, { id: `u-${Date.now()}`, role: 'user', text }]);
    setInput('');
    setTimeout(() => {
      setMsgs(prev => [...prev, { id: `a-${Date.now()}`, role: 'ai', text: 'Je recherche les meilleurs prestataires disponibles…' }]);
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 600);
  }, [input]);

  // ── Enregistrement audio ────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob    = new Blob(audioChunksRef.current, { type: mimeType });
        const audioUrl = URL.createObjectURL(blob);
        setMsgs(prev => [...prev, { id: `u-${Date.now()}`, role: 'user', audioUrl }]);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
      };

      recorder.start();
      setIsRecording(true);
    } catch {
      // Micro non disponible ou refusé
    }
  }, []);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording();
    else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  if (!ottoOpen) return null;

  const headerBtn = (icon: React.ReactNode, onClick: () => void): React.ReactNode => (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 28, height: 28, borderRadius: 7,
        background: 'rgba(255,255,255,.07)',
        border: '0.5px solid rgba(255,255,255,.12)',
        color: 'rgba(255,255,255,.6)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {icon}
    </button>
  );

  return (
    <div
      style={{
        position:  'fixed',
        bottom:    `calc(${bottomOffset}px + env(safe-area-inset-bottom))`,
        transition:'bottom .15s ease, height .25s ease',
        left:       10,
        right:      10,
        zIndex:     800,
      }}
    >
      {/* Bulle principale */}
      <div style={{
        height,
        transition:   'height .25s ease',
        background:   'rgba(10,5,2,0.95)',
        border:       '0.5px solid rgba(220,38,38,0.30)',
        borderRadius:  14,
        backdropFilter:'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display:      'flex',
        flexDirection:'column',
        overflow:     'hidden',
      }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 10px 6px', flexShrink: 0 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E', animation: 'blink 1.5s infinite' }} />
        <span style={{ fontSize: 9, fontWeight: 700, color: '#fca5a5', letterSpacing: '.06em', textTransform: 'uppercase', flex: 1 }}>
          Otto
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {headerBtn(
            size === 'compact' ? <IconExpand /> : <IconCollapse />,
            () => setSize(s => s === 'compact' ? 'half' : 'compact'),
          )}
          {headerBtn(<IconClose />, () => setOttoOpen(false))}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px 4px', display: 'flex', flexDirection: 'column', gap: 4, scrollbarWidth: 'none' }}>
        {msgs.map(m => (
          <div
            key={m.id}
            style={{
              maxWidth: '85%',
              ...(m.role === 'ai'
                ? { alignSelf: 'flex-start' }
                : { alignSelf: 'flex-end' }),
            }}
          >
            {m.audioUrl ? (
              <div style={{ background: m.role === 'user' ? 'rgba(5,150,105,.13)' : 'rgba(220,38,38,.10)', border: `0.5px solid ${m.role === 'user' ? 'rgba(5,150,105,.22)' : 'rgba(220,38,38,.18)'}`, borderRadius: 7, padding: '6px 8px' }}>
                <audio src={m.audioUrl} controls style={{ height: 28, maxWidth: 180 }} />
              </div>
            ) : (
              <div style={{ fontSize: 12, lineHeight: 1.5, padding: '6px 9px', borderRadius: 7, ...(m.role === 'ai' ? { background: 'rgba(220,38,38,.10)', border: '0.5px solid rgba(220,38,38,.18)', color: 'rgba(255,255,255,.78)' } : { background: 'rgba(5,150,105,.13)', border: '0.5px solid rgba(5,150,105,.22)', color: 'rgba(255,255,255,.78)' }) }}>
                {m.text}
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 4, padding: '5px 8px 8px', borderTop: '0.5px solid rgba(255,255,255,.06)', flexShrink: 0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Écrivez ou parlez…"
          style={{
            flex: 1, height: 38,
            background: 'rgba(255,255,255,.06)',
            border: '0.5px solid rgba(255,255,255,.10)',
            borderRadius: 8, padding: '0 10px',
            color: 'rgba(255,255,255,.8)',
            fontSize: 16, outline: 'none',
          }}
        />
        {/* Micro */}
        <button
          type="button"
          onClick={toggleRecording}
          title={isRecording ? 'Arrêter l\'enregistrement' : 'Message vocal'}
          style={{
            width: 38, height: 38, borderRadius: 8,
            background: isRecording ? 'rgba(220,38,38,.25)' : 'rgba(255,255,255,.06)',
            border: isRecording ? '0.5px solid rgba(220,38,38,.5)' : '0.5px solid rgba(255,255,255,.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: isRecording ? '#f87171' : 'rgba(255,255,255,.5)',
            animation: isRecording ? 'blink 1s infinite' : 'none',
          }}
        >
          <IconMic recording={isRecording} />
        </button>
        {/* Envoyer */}
        <button
          type="button"
          onClick={send}
          style={{ width: 38, height: 38, borderRadius: 8, background: 'rgba(220,38,38,.35)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <IconSend />
        </button>
      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
      `}</style>
      </div>{/* fin bulle principale */}

      {/* Triangle/queue — pointe vers le bouton Otto centré */}
      <div style={{ position: 'absolute', bottom: -9, left: 'calc(50% - 9px)', width: 0, height: 0, borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderTop: '9px solid rgba(220,38,38,0.30)' }} />
      <div style={{ position: 'absolute', bottom: -8, left: 'calc(50% - 8px)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid rgba(10,5,2,0.95)' }} />
    </div>
  );
}
